import { autoinject, transient, computedFrom, observable } from "aurelia-framework";
import { DialogClientService } from "@rightship-tech/common-ui/services/basic/dialog-client.service";
import { IncidentService } from "../../../../../../../services/incident/incident.service";
import { ValidationActivation, ValidationTypeEnum } from "../../../../../../../client-validation-system/validations.models";
import { EntityValidationsService, ClientSideValidator } from "../../../../../../../client-validation-system/validations-service";
import { ValidationsClientService } from "../../../../../../../services/validations/validations-client.service";
import { LookupValue } from "../../../../../../../models/lookups/lookup.models";
import { ComputedIncidentSeverityEnum, IncidentReasonForOverrideEnum } from "../../../../../../../models/incidents/incident.models";
import { Operation } from "../../../../../../../client-validation-system/base-validations/compare-validation";
import moment from "moment";

@autoinject
@transient()
export class IncidentDetailsOverrideDialog {

    _incidentReasonForOverride: LookupValue;

    public attachments: File[] = [];
    public incidentOverrideId: number;
    public incidentId: number;
    public vesselId: number;
    public isClosed: boolean;
    public computedSeverityId: number;
    public reload: any;
    public incidentReasonForOverrideId: number;
    public incidentSeverityOverrideId: number;
    public justification: string;
    public validator: ClientSideValidator;
    public overridenGracePeriod: number;
    public excludedReasonsById: number[] = [];

    @observable()
    public gracePeriodValidForDate: string;

    constructor(private dialogClientService: DialogClientService,
        private incidentClientService: IncidentService,
        private entityValidationsService: EntityValidationsService,
        private validationsClientService: ValidationsClientService) {
    }

    activate(params) {
        this.reload = params.reload;
        this.incidentId = params.incidentId;
        this.vesselId = params.vesselId;
        this.isClosed = params.isClosed;
        this.computedSeverityId = params.computedSeverityId;
        this.excludedReasonsById = params.remainingGracePeriod > 0
            ? [IncidentReasonForOverrideEnum.IncidentShouldAffectVesselSafetyScore, IncidentReasonForOverrideEnum.IncidentShouldNotAffectVesselSafetyScore, IncidentReasonForOverrideEnum.IncidentWasNonFault]
            : [];
    }

    attached() {
        this.initRules();
    }

    set incidentReasonForOverride(value: LookupValue) {
        this._incidentReasonForOverride = value;
        this.initRules();
    }

    @computedFrom("_incidentReasonForOverride")
    get showSeverityOverride() {
        return this._incidentReasonForOverride && this._incidentReasonForOverride.additionalData
            && Object.keys(this._incidentReasonForOverride.additionalData).indexOf("shouldOverwiteServerity") > -1
            && this._incidentReasonForOverride.additionalData["shouldOverwiteServerity"] == true;
    }

    @computedFrom("_incidentReasonForOverride", "incidentReasonForOverrideId")
    get showGracePeriodValidity() {
        return this._incidentReasonForOverride && this.incidentReasonForOverrideId == IncidentReasonForOverrideEnum.ChangeSafetyScoreGracePeriod;
    }

    @computedFrom("gracePeriodValidForDate")
    get gracePeriodValidFor(): number {
        if (!this.gracePeriodValidForDate) {
            return null;
        }
        const today = moment(new Date()).startOf('day');
        const validFor = moment(this.gracePeriodValidForDate).startOf('day');
        const daysDiff = validFor.diff(today, 'days');
        return daysDiff;
    }

    gracePeriodValidForDateChanged() {
        this.incidentClientService.computeIncidentGracePeriod({
            incidentId: this.incidentId,
            remainingDays: this.gracePeriodValidFor
        }).then(response => {
            this.overridenGracePeriod = response.gracePeriod;
        });
    }

    initRules() {
        if (this.validator)
            this.validator.dispose();

        const rulesHolder = {
            activation: ValidationActivation.Always,
            entityName: "Incident Override",
            isCollection: false,
            nestedRulesHolder: [],
            rules: [{
                errorMessage: "Reason for Override is required",
                parameters: null, property: "incidentReasonForOverrideId",
                validationType: ValidationTypeEnum.Required
            }, {
                errorMessage: "Justification is required",
                parameters: null, property: "justification",
                validationType: ValidationTypeEnum.Required
            }]
        };

        if (this.showSeverityOverride) {
            rulesHolder.rules.push({
                errorMessage: "Severity Override is required",
                parameters: null, property: "incidentSeverityOverrideId",
                validationType: ValidationTypeEnum.Required,
            });
        }

        if (this.incidentReasonForOverrideId == IncidentReasonForOverrideEnum.IncidentShouldAffectVesselSafetyScore) {
            rulesHolder.rules.push({
                errorMessage: "That override reason cannot be chosen when severity is Category D",
                parameters: {
                    operation: Operation.LessThan,
                    property: "computedSeverityId",
                    value: ComputedIncidentSeverityEnum.CategoryD

                }, property: "incidentReasonForOverrideId",
                validationType: ValidationTypeEnum.Compare,

            });
        }

        if (this.showGracePeriodValidity) {
            rulesHolder.rules.push({
                errorMessage: "Grace Period Override date is required",
                parameters: null, property: "gracePeriodValidForDate",
                validationType: ValidationTypeEnum.Required,
            });
        }

        this.validator = this.entityValidationsService.getValidator(this, rulesHolder);
    }

    cancel() {
        this.dialogClientService.hide();
    }

    async save(submit: boolean) {
        if (submit) {
            this.initRules();
            const res = await this.validator.validate();
            if (!res.valid) return;
        }

        try {
            const response = await this.incidentClientService.overrideDetails({
                incidentOverrideId: this.incidentOverrideId,
                incidentId: this.incidentId,
                incidentReasonForOverrideId: this.incidentReasonForOverrideId,
                overridenComputedSeverityId: this.showSeverityOverride ? this.incidentSeverityOverrideId : null,
                justification: this.justification,
                overridenGracePeriod: this.showGracePeriodValidity ? this.overridenGracePeriod : null,
                overridenUseInSafetyScore: this.gracePeriodValidFor === null ? null : this.gracePeriodValidFor <= 0
            }, submit, this.vesselId, this.isClosed, this.attachments);

            if (response.validationResult && response.validationResult.hasError) {
                this.validationsClientService.showValidationErrors(response.validationResult);
            } else {
                this.incidentOverrideId = response.id;

                if (submit) {
                    if (this.reload) this.reload();
                    this.cancel();
                }
            }
        } catch (exception) {
            if (exception.validationResult && exception.validationResult.hasError) {
                this.validationsClientService.showValidationErrors(exception.validationResult);
            }
        }
    }

    today() {
        const date = moment(new Date()).startOf('day');
        return date;
    }

    getFormattedDate(date) {
        return moment(date).format('DD MMM YYYY');
    }
}